import { useSyncOnChange } from '~/hooks/use-sync-on-change'
import { paginate } from '~/pages/backoffice/paginate'
import { DEFAULT_PAGE_SIZE, useTableQuery } from '~/pages/backoffice/useTableQuery'

/**
 * Pagination d'une liste chargée en entier et filtrée côté client — modules,
 * parcours, groupes, analyse des questions (#198).
 *
 * Même état de page que les tableaux paginés par l'API (`useTableQuery`), pour
 * que « lignes par page » s'y comporte pareil. `pagination` se passe tel quel
 * à `TablePagination`.
 *
 * `filtersKey` résume la recherche et les filtres de l'écran : quand il change,
 * retour en page 1. Une clé plutôt qu'un `resetPage` à appeler depuis chaque
 * filtre : `useListSearch` réagit pendant son propre appel, avant que la
 * pagination n'existe, et un écran à cinq filtres en oublierait un.
 */
export function useClientPagination<T>(rows: readonly T[], filtersKey: string, initialPageSize: number = DEFAULT_PAGE_SIZE) {
  const { page, pageSize, setPage, setPageSize, resetPage } = useTableQuery(initialPageSize)
  useSyncOnChange(filtersKey, resetPage)
  const current = paginate(rows, page, pageSize)
  // La page affichée devient la page retenue : sans ça, une page bornée après
  // une suppression restait 3 en mémoire, et l'écran y sautait de lui-même au
  // prochain import — comme « lignes par page » repartait d'une page fantôme.
  useSyncOnChange(current.page, setPage)

  return {
    pageRows: current.rows,
    pagination: {
      page: current.page,
      pageSize,
      total: current.total,
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
    },
  }
}
